import { styled } from '@mui/material/styles';
import { Box, Typography, Button, Card } from '@mui/material';

export const Page = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '100vh',
  background: 'transparent',
}));

export const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(10, 0, 8),
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background: 'radial-gradient(circle at 25% 30%, rgba(28,180,216,0.1) 0%, transparent 35%), radial-gradient(circle at 75% 20%, rgba(124,58,237,0.1) 0%, transparent 30%)',
    opacity: 0.9,
  },
}));

export const Glow = styled(Box)(({ theme, size = '460px', top = '0', left = '0' }) => ({
  position: 'absolute',
  width: size,
  height: size,
  top,
  left,
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(28, 180, 216, 0.16) 0%, rgba(15, 149, 189, 0.04) 70%)',
  filter: 'blur(60px)',
  opacity: 0.7,
  mixBlendMode: 'screen',
  pointerEvents: 'none',
}));

export const HeroGrid = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: theme.spacing(4),
  alignItems: 'center',
  zIndex: 1,
}));

export const HeroContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

export const MetaBar = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.25, 1.75),
  borderRadius: 999,
  border: `1px solid ${theme.palette.primary.main}55`,
  background: theme.customGradients?.glass,
  backdropFilter: 'blur(12px)',
  width: 'fit-content',
  boxShadow: theme.shadows[6],
}));

export const HeroTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: 'clamp(2.5rem, 5vw, 3.8rem)',
  lineHeight: 1.05,
  background: theme.customGradients?.brand,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

export const HeroSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  maxWidth: 720,
  lineHeight: 1.6,
}));

export const CTAButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  fontSize: '1.05rem',
  fontWeight: 700,
  boxShadow: theme.shadows[10],
  borderRadius: 14,
}));

export const GhostButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.4, 3.6),
  borderRadius: 14,
  border: `1px solid ${theme.palette.primary.main}55`,
  color: theme.palette.text.primary,
  background: 'rgba(255,255,255,0.04)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    background: 'rgba(255,255,255,0.07)',
    borderColor: theme.palette.primary.main,
  },
}));

export const FloatingPanel = styled(Box)(({ theme }) => ({
  background: theme.customGradients?.glass,
  border: `1px solid ${theme.palette.primary.main}40`,
  borderRadius: theme.shape.borderRadius * 1.2,
  padding: theme.spacing(3),
  boxShadow: theme.shadows[12],
  backdropFilter: 'blur(14px)',
}));

export const FloatingMetric = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.25, 1.5),
  borderRadius: theme.shape.borderRadius,
  background: 'rgba(255,255,255,0.03)',
  border: `1px solid ${theme.palette.primary.main}30`,
  '& + &': {
    marginTop: theme.spacing(1),
  },
}));

export const MiniStat = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  padding: theme.spacing(1.75),
  borderRadius: theme.shape.borderRadius,
  background: 'linear-gradient(145deg, rgba(28,180,216,0.11), rgba(124,58,237,0.08))',
  border: `1px solid ${theme.palette.primary.main}30`,
  minWidth: 180,
  boxShadow: theme.shadows[8],
}));

export const StatsBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2.5),
  background: theme.customGradients?.glass,
  borderRadius: theme.shape.borderRadius * 1.1,
  border: `1px solid ${theme.palette.primary.main}33`,
  boxShadow: theme.shadows[10],
}));

export const StatsNumber = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  color: theme.palette.primary.light,
  marginBottom: theme.spacing(1),
  letterSpacing: '-0.02em',
}));

export const SectionHeading = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  textAlign: 'center',
  marginBottom: theme.spacing(1),
  letterSpacing: '-0.02em',
}));

export const SectionSubheading = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.text.secondary,
  maxWidth: 720,
  margin: '0 auto',
  marginBottom: theme.spacing(5),
}));

export const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(3),
  textAlign: 'left',
  borderRadius: theme.shape.borderRadius * 1.1,
  position: 'relative',
  overflow: 'hidden',
  transition: 'transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.08), transparent 40%)',
    pointerEvents: 'none',
  },
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: theme.shadows[16],
    borderColor: theme.palette.primary.main,
  },
}));

export const FeatureIcon = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.light,
  marginBottom: theme.spacing(2),
  display: 'inline-flex',
  padding: theme.spacing(1.1),
  borderRadius: theme.shape.borderRadius,
  background: 'linear-gradient(145deg, rgba(28,180,216,0.13), rgba(124,58,237,0.12))',
  border: `1px solid ${theme.palette.primary.main}33`,
}));

export const StepCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(2.25),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.primary.main}30`,
  background: theme.customGradients?.glass,
  boxShadow: theme.shadows[6],
}));

export const StepNumber = styled(Box)(({ theme }) => ({
  width: 46,
  height: 46,
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  background: 'linear-gradient(145deg, rgba(28,180,216,0.18), rgba(124,58,237,0.16))',
  color: theme.palette.background.default,
  fontWeight: 800,
  border: `1px solid ${theme.palette.primary.main}40`,
}));

export const BenefitCard = styled(Card)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(2.75),
  borderRadius: theme.shape.borderRadius * 1.1,
  textAlign: 'left',
  background: 'linear-gradient(160deg, rgba(28,180,216,0.11), rgba(15,23,42,0.9))',
  border: `1px solid ${theme.palette.primary.main}28`,
  boxShadow: theme.shadows[10],
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[16],
  },
}));

export const CTASection = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(8, 0),
  background: 'linear-gradient(135deg, rgba(28,180,216,0.12), rgba(124,58,237,0.12))',
  borderTop: `1px solid ${theme.palette.primary.main}26`,
  borderBottom: `1px solid ${theme.palette.primary.main}26`,
  overflow: 'hidden',
}));

export const CTAInner = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 1.2,
  background: 'linear-gradient(145deg, rgba(12,18,32,0.85), rgba(28,180,216,0.07))',
  border: `1px solid ${theme.palette.primary.main}30`,
  boxShadow: theme.shadows[14],
  backdropFilter: 'blur(12px)',
}));
